import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useAxios from '../api/axios';
import CompetitionCard from '../components/CompetitionCard';
import { Button } from '../components/Button';
import useAuth from '../hooks/useAuth';

const parseCompetitions = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.competitions)) {
    return data.competitions;
  }

  if (Array.isArray(data?.companies)) {
    return data.companies;
  }

  return [];
};

const getCompetitionStartDate = (competition) => (
  competition?.startDate || competition?.startdate || competition?.start_date || null
);

const getCurrentUserID = (auth) => {
  const possibleUserID = auth?.user?.userID
    ?? auth?.user?.ID
    ?? auth?.user?.userObj?.userID
    ?? auth?.user?.userObj?.ID
    ?? null;

  return possibleUserID !== null && possibleUserID !== undefined
    ? Number(possibleUserID)
    : null;
};

const isCompetitionNotStarted = (competition) => {
  const startDate = getCompetitionStartDate(competition);

  if (!startDate) {
    return true;
  }

  const parsedStartDate = new Date(startDate);

  if (Number.isNaN(parsedStartDate.getTime())) {
    return true;
  }

  return parsedStartDate > new Date();
};

const getWinnerUserID = (pair) => {
  const winner = pair?.winnerUserID ?? null;

  if (winner === null || winner === undefined || Number(winner) === 0) {
    return null;
  }

  return Number(winner);
};

const getUserSlot = (pair, userID) => {
  if (!pair || userID === null || userID === undefined) {
    return null;
  }

  if (Number(pair.userID1 ?? pair.metrixUserID1 ?? null) === Number(userID)) {
    return 1;
  }

  if (Number(pair.userID2 ?? pair.metrixUserID2 ?? null) === Number(userID)) {
    return 2;
  }

  return null;
};

const getCompetitionProgress = (pairs, userID) => {
  if (userID === null || userID === undefined) {
    return { include: false, statusText: 'Kirjaudu nähdäksesi omat pelisi' };
  }

  if (!Array.isArray(pairs) || pairs.length === 0) {
    return { include: true, statusText: 'Odottaa parien muodostusta' };
  }

  let hasUserPair = false;
  let hasActiveMatch = false;
  let hasLost = false;

  pairs.forEach((pair) => {
    const userSlot = getUserSlot(pair, userID);

    if (!userSlot) {
      return;
    }

    hasUserPair = true;

    const winnerUserID = getWinnerUserID(pair);

    if (winnerUserID === null) {
      hasActiveMatch = true;
      return;
    }

    const winningUserSlot = getUserSlot(pair, winnerUserID);
    if (winningUserSlot !== userSlot) {
      hasLost = true;
    }
  });

  if (hasLost) {
    return { include: false, statusText: 'Pudonnut kilpailusta' };
  }

  const finalPair = pairs.find((pair) => Number(pair?.level) === 1) ?? null;
  const finalWinnerUserID = getWinnerUserID(finalPair);
  const finalUserSlot = getUserSlot(finalPair, userID);
  const finalWinnerSlot = getUserSlot(finalPair, finalWinnerUserID);

  if (finalPair && finalWinnerSlot !== null && finalUserSlot !== null && finalWinnerSlot === finalUserSlot) {
    return { include: false, statusText: 'Kilpailu päättynyt' };
  }

  if (hasActiveMatch) {
    return { include: true, statusText: 'Ottelu käynnissä' };
  }

  if (!hasUserPair) {
    return { include: true, statusText: 'Odottaa ensimmäistä ottelua' };
  }

  return { include: true, statusText: 'Odottaa seuraavaa ottelua' };
};

function MyCompetition() {
  const navigate = useNavigate();
  const location = useLocation();
  const api = useAxios();
  const { auth, isAdmin, isAuthenticated } = useAuth();

  const [competitions, setCompetitions] = useState([]);
  const [registeredMap, setRegisteredMap] = useState(new Map());
  const [progressMap, setProgressMap] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [expandedID, setExpandedID] = useState(null);

  const userID = useMemo(() => getCurrentUserID(auth), [auth]);

  useEffect(() => {
    if (!isAuthenticated()) {
      setCompetitions([]);
      setRegisteredMap(new Map());
      setLoading(false);
      return;
    }

    let isMounted = true;

    const loadInitialData = async () => {
      setLoading(true);

      try {
        const [competitionsResponse, registrationsResponse] = await Promise.all([
          api.post(
            '/getCompetitions',
            JSON.stringify({}),
            { headers: { 'Content-Type': 'application/json' } }
          ),
          api.post(
            '/getMyRegistrations',
            JSON.stringify({}),
            { headers: { 'Content-Type': 'application/json' } }
          ),
        ]);

        if (!isMounted) {
          return;
        }

        const nextCompetitions = parseCompetitions(competitionsResponse?.data);
        const nextRegisteredMap = new Map();

        if (Array.isArray(registrationsResponse?.data)) {
          registrationsResponse.data.forEach((item) => {
            if (item !== null && typeof item === 'object') {
              nextRegisteredMap.set(Number(item.competitionID), item.classID ?? null);
              return;
            }

            nextRegisteredMap.set(Number(item), null);
          });
        }

        setCompetitions(nextCompetitions);
        setRegisteredMap(nextRegisteredMap);
      } catch (err) {
        if (!isMounted) {
          return;
        }

        const message = err?.response?.data?.message || err?.response?.data?.msg;
        toast.error(message || 'Omien pelien haku epäonnistui');
        setCompetitions([]);
        setRegisteredMap(new Map());
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadInitialData();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, userID]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAuthenticated() || registeredMap.size === 0) {
      setProgressMap(new Map());
      return;
    }

    const startedCompetitions = competitions.filter((competition) => {
      const competitionID = Number(competition?.competitionID);
      return registeredMap.has(competitionID) && !isCompetitionNotStarted(competition);
    });

    if (startedCompetitions.length === 0) {
      setProgressMap(new Map());
      return;
    }

    let cancelled = false;

    const loadCompetitionProgress = async () => {
      const progressEntries = await Promise.all(
        startedCompetitions.map(async (competition) => {
          const competitionID = Number(competition.competitionID);
          const classID = registeredMap.get(competitionID);

          try {
            const response = await api.post(
              '/getPairs',
              JSON.stringify({
                competitionID,
                ...(classID !== null && classID !== undefined ? { classID: Number(classID) } : {}),
              }),
              { headers: { 'Content-Type': 'application/json' } }
            );

            return [competitionID, getCompetitionProgress(response?.data, userID)];
          } catch {
            return [competitionID, { include: true, statusText: 'Pelien tila ei saatavilla' }];
          }
        })
      );

      if (!cancelled) {
        setProgressMap(new Map(progressEntries));
      }
    };

    loadCompetitionProgress();

    return () => {
      cancelled = true;
    };
  }, [competitions, isAuthenticated, registeredMap, userID]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleCompetitions = useMemo(() => competitions.filter((competition) => {
    const competitionID = Number(competition?.competitionID);

    if (!registeredMap.has(competitionID)) {
      return false;
    }

    if (isCompetitionNotStarted(competition)) {
      return true;
    }

    const progress = progressMap.get(competitionID);
    return progress ? progress.include : true;
  }), [competitions, progressMap, registeredMap]);

  const handleCancelRegistration = async (competitionID) => {
    try {
      await api.post(
        '/cancelRegistration',
        JSON.stringify({ competitionID }),
        { headers: { 'Content-Type': 'application/json' } }
      );

      setRegisteredMap((prev) => {
        const next = new Map(prev);
        next.delete(Number(competitionID));
        return next;
      });

      setProgressMap((prev) => {
        const next = new Map(prev);
        next.delete(Number(competitionID));
        return next;
      });

      toast.success('Rekisteröinti poistettu.');
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.msg;
      toast.error(message || 'Rekisteröinnin poistaminen epäonnistui');
    }
  };

  const handleUpdateRegistration = async (competitionID, classID = null) => {
    try {
      await api.post(
        '/updateRegistration',
        JSON.stringify({ competitionID, ...(classID !== null ? { classID } : {}) }),
        { headers: { 'Content-Type': 'application/json' } }
      );

      setRegisteredMap((prev) => new Map([...prev, [Number(competitionID), classID ?? null]]));
      toast.success('Rekisteröinti päivitetty.');
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.msg;
      toast.error(message || 'Rekisteröinnin päivittäminen epäonnistui');
    }
  };

  if (!isAuthenticated()) {
    return (
      <section className="mx-auto max-w-5xl px-4 pb-12 pt-24">
        <div className="rounded-[28px] border border-white/10 bg-slate-900/70 p-6 shadow-2xl shadow-slate-950/30 backdrop-blur">
          <p className="text-sm font-semibold uppercase tracking-[0.35em] text-cyan-300">Omat pelit</p>
          <h1 className="mt-2 text-3xl font-black text-white md:text-4xl">Kirjaudu nähdäksesi omat pelisi</h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300">
            Sivu näyttää kilpailut, joihin olet rekisteröitynyt ja joissa sinulla on vielä otteluita jäljellä.
          </p>
          <div className="mt-6 flex gap-3">
            <Button name="Kirjaudu" type="PRIMARY" action={() => navigate('/login', { state: { from: location } })} />
            <Link to="/kisat" className="rounded-full border border-white/15 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/10">
              Siirry kilpailuihin
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-6xl px-4 pb-12 pt-24">
      <div className="rounded-[28px] border border-white/10 bg-slate-900/70 p-6 shadow-2xl shadow-slate-950/30 backdrop-blur md:flex md:items-center md:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.35em] text-cyan-300">Omat pelit</p>
          <h1 className="mt-2 text-3xl font-black text-white md:text-4xl">Kilpailut joissa olet edelleen mukana</h1>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-300">
            Näytössä ovat kilpailut, joihin olet rekisteröitynyt ja jotka eivät ole vielä alkaneet tai joissa et ole vielä pudonnut.
          </p>
        </div>

        <div className="mt-4 md:mt-0">
          {isAdmin && isAdmin() ? (
            <Button name="Lisää uusi" type="PRIMARY" action={() => navigate('/lisaa_kisa')} />
          ) : null}
        </div>
      </div>

      {loading ? (
        <div className="mt-6 rounded-2xl border border-dashed border-white/15 bg-white/5 px-6 py-10 text-center text-slate-300">
          <p className="text-lg font-semibold text-white">Ladataan omia pelejä...</p>
        </div>
      ) : visibleCompetitions.length === 0 ? (
        <div className="mt-6 rounded-[28px] border border-slate-200 bg-white/95 px-6 py-8 shadow-[0_20px_70px_-30px_rgba(15,23,42,0.45)]">
          <p className="text-lg font-semibold text-slate-900">Sinulla ei ole aktiivisia kilpailuja.</p>
          <p className="mt-2 text-sm text-slate-600">
            Lista näyttää vain kilpailut, jotka eivät ole vielä alkaneet tai joissa sinulla on edelleen mahdollisuus jatkaa.
          </p>
          <div className="mt-5">
            <Link to="/kisat" className="rounded-full bg-cyan-600 px-5 py-2 text-sm font-bold text-white transition hover:bg-cyan-500">
              Selaa kilpailuja
            </Link>
          </div>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {visibleCompetitions.map((competition) => {
            const competitionID = Number(competition.competitionID);
            const progress = progressMap.get(competitionID);
            const isUpcoming = isCompetitionNotStarted(competition);
            const statusText = isUpcoming ? 'Ei ole vielä alkanut' : progress?.statusText ?? 'Kilpailu käynnissä';

            return (
              <div key={competitionID} className="space-y-2">
                <div className="flex items-center gap-2 px-2">
                  <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.25em] ${
                    isUpcoming
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {statusText}
                  </span>
                </div>

                <CompetitionCard
                  competition={competition}
                  isRegistered={registeredMap.has(competitionID)}
                  registeredClassID={registeredMap.get(competitionID) ?? null}
                  isExpanded={expandedID === competitionID}
                  onExpand={() => setExpandedID(expandedID === competitionID ? null : competitionID)}
                  onClose={() => setExpandedID(null)}
                  onRegister={() => {}}
                  onCancelRegistration={handleCancelRegistration}
                  onUpdateRegistration={handleUpdateRegistration}
                />
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default MyCompetition;